import { useState } from 'react';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { ClipboardList, Ellipsis, Settings2, UserPlus, Users as UsersIcon } from 'lucide-react';
import { Card, CardHeader } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { PageHeader, PageShell } from '../../components/ui/PageHeader';
import { EmptyState, ErrorState, LoadingState } from '../../components/ui/States';
import { Pagination } from '../../components/ui/Pagination';
import { Table, THead, Th, Td, Tr } from '../../components/ui/Table';
import { useAuth } from '../../hooks/useAuth';
import { useTeams } from '../../hooks/useTeams';
import {
  useUsers,
  useUpdateUser,
  useDeactivateUser,
  useInviteUser,
  usePendingInvites,
  useDeleteInvite,
  useResendInvite,
  useAuditLogs,
} from '../../hooks/useUsers';
import type { AuditLog, User } from '../../api/users';
import { formatDayMonthTime, formatDayMonthYear } from '../../utils/dateFormat';
import { UserAccessModal, RoleDefaultsModal } from './AccessModals';
import { TeamsModal } from './TeamsModal';

/** Exactly one modal at a time — opening another replaces the current one. */
type OpenModal =
  | { kind: 'invite' }
  | { kind: 'audit' }
  | { kind: 'roleDefaults' }
  | { kind: 'teams' }
  | { kind: 'edit'; user: User }
  | { kind: 'access'; user: User }
  | null;

/** Neutral initials avatar: "Walter Grandjean" → "WG". */
function Avatar({ name, muted = false }: { name: string; muted?: boolean }) {
  const initials =
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]!.toUpperCase())
      .join('') || '?';
  return (
    <span
      aria-hidden
      className={`flex h-8 w-8 flex-none items-center justify-center rounded-full bg-surface-tertiary text-body-xs font-medium ${muted ? 'text-text-muted' : 'text-text-secondary'}`}
    >
      {initials}
    </span>
  );
}

const ROLE_ORDER = ['owner', 'admin', 'member', 'viewer'];

/** Sort position of a role; unknown roles go last. */
function roleRank(role: string): number {
  const i = ROLE_ORDER.indexOf(role);
  return i === -1 ? ROLE_ORDER.length : i;
}

const DORMANT_MS = 60 * 86_400_000;

/** No sign-in for 60+ days (or never, on an account older than that). */
function isDormant(user: User, now = Date.now()): boolean {
  if (!user.isActive) return false;
  const last = user.lastLoginAt ?? user.createdAt;
  return now - new Date(last).getTime() > DORMANT_MS;
}

function roleLabel(role: string): string {
  return role.charAt(0).toUpperCase() + role.slice(1);
}

export function Users() {
  const { user: currentUser } = useAuth();
  const [modal, setModal] = useState<OpenModal>(null);
  const close = () => setModal(null);

  const { data: usersData, isLoading: usersLoading, error: usersError, refetch } = useUsers();
  const { data: invitesData } = usePendingInvites();
  const { data: teamsData, isLoading: teamsLoading, error: teamsError } = useTeams();
  const inviteUser = useInviteUser();
  const updateUser = useUpdateUser();
  const deactivateUser = useDeactivateUser();
  const deleteInvite = useDeleteInvite();
  const resendInvite = useResendInvite();

  const isAdmin = currentUser?.role === 'admin';

  if (!isAdmin) {
    return (
      <PageShell>
        <ErrorState title="You don’t have permission to access this page." />
      </PageShell>
    );
  }

  // Owners and admins first, then members and viewers; by name within a role.
  const users = [...(usersData?.users ?? [])].sort(
    (a, b) =>
      roleRank(a.role) - roleRank(b.role) ||
      (a.name || a.email).localeCompare(b.name || b.email, undefined, { sensitivity: 'base' })
  );
  const pendingInvites = invitesData?.invites || [];
  const teamsOf = (userId: string) => (teamsData?.teams ?? []).filter((t) => t.memberIds.includes(userId));
  const dormantCount = users.filter((u) => isDormant(u)).length;

  /** What a user can reach: admins everything; others their teams, viewers read-only. */
  const accessCell = (user: User) => {
    if (user.role === 'admin' || user.role === 'owner') return <span className="text-text-tertiary">Full access</span>;
    if (teamsLoading) return <span className="text-text-muted">…</span>;
    if (teamsError) return <span className="text-text-muted" title="Teams could not be loaded">Unavailable</span>;
    const teams = teamsOf(user.id);
    const names = teams.map((t) => t.name).join(', ');
    if (user.role === 'viewer') {
      return (
        <span className="text-text-secondary" title="Viewers see only what their teams grant, read-only">
          {names ? `Locked to ${names}` : 'Role defaults'} · view only
        </span>
      );
    }
    return <span className="text-text-secondary">{names || <span className="text-text-tertiary">Role defaults</span>}</span>;
  };

  return (
    <PageShell>
      <PageHeader eyebrow="Admin"
        title="Users"
        subtitle="Users, roles and what each of them can see."
        actions={
          <>
            <Button variant="secondary" leftIcon={<UsersIcon className="h-4 w-4" />} onClick={() => setModal({ kind: 'teams' })}>
              Teams
            </Button>
            <Button
              variant="secondary"
              leftIcon={<Settings2 className="h-4 w-4" />}
              onClick={() => setModal({ kind: 'roleDefaults' })}
            >
              Role defaults
            </Button>
            <Button
              variant="secondary"
              leftIcon={<ClipboardList className="h-4 w-4" />}
              onClick={() => setModal({ kind: 'audit' })}
            >
              Audit log
            </Button>
            <Button leftIcon={<UserPlus className="h-4 w-4" />} onClick={() => setModal({ kind: 'invite' })}>
              Invite user
            </Button>
          </>
        }
      />

      {usersLoading ? (
        <LoadingState rows={5} />
      ) : usersError ? (
        <ErrorState title="Couldn’t load users" error={usersError} onRetry={() => refetch()} />
      ) : (
        <>
          {pendingInvites.length > 0 && (
            <Card>
              <CardHeader title="Pending invites" description={`${pendingInvites.length} waiting to be accepted`} />
              <div className="space-y-2">
                {pendingInvites.map((invite) => (
                  <div
                    key={invite.id}
                    className="flex items-center justify-between border-b border-border-light py-3 last:border-0"
                  >
                    <div>
                      <p className="text-body-sm font-medium text-text-primary">{invite.email}</p>
                      <p className="text-body-xs text-text-tertiary">
                        {roleLabel(invite.role)} · Expires {formatDayMonthYear(invite.expiresAt)}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={resendInvite.isPending}
                        onClick={() => resendInvite.mutate(invite.id)}
                      >
                        {resendInvite.isPending ? 'Sending…' : 'Resend'}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          if (confirm('Cancel this invitation?')) deleteInvite.mutate(invite.id);
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          <Card>
            <CardHeader
              title={`${users.length} ${users.length === 1 ? 'user' : 'users'}`}
              description={dormantCount > 0 ? `${dormantCount} dormant: no sign-in for more than 60 days` : undefined}
            />
            <Table minWidth={760} colWidths={['34%', '12%', '26%', '20%', '8%']}>
              <THead>
                <tr>
                  <Th>User</Th>
                  <Th>Role</Th>
                  <Th>Team / access</Th>
                  <Th>Last sign-in</Th>
                  <Th aria-label="Actions" />
                </tr>
              </THead>
              <tbody>
                {users.map((user) => (
                  <Tr key={user.id} className="last:border-0">
                    <Td className="py-3">
                      <div className="flex items-center gap-3">
                        <Avatar name={user.name} muted={!user.isActive} />
                        <div className="min-w-0">
                          <p className="flex items-center gap-2 text-body-sm font-medium text-text-primary">
                            {user.name}
                            {/* Status only when it is the exception; every other row is active */}
                            {!user.isActive && <Badge>Inactive</Badge>}
                          </p>
                          <p className="text-body-xs text-text-tertiary">{user.email}</p>
                        </div>
                      </div>
                    </Td>
                    <Td className="py-3">
                      <Badge variant="default" size="sm">
                        {roleLabel(user.role)}
                      </Badge>
                    </Td>
                    <Td className="py-3">
                      <span className="text-body-xs">{accessCell(user)}</span>
                    </Td>
                    <Td className="py-3">
                      <span className="text-body-xs text-text-tertiary">
                        {user.lastLoginAt ? formatDayMonthTime(user.lastLoginAt) : 'Never'}
                      </span>
                      {isDormant(user) && (
                        <span className="ml-1.5 text-body-xs text-warning-dark" title="No sign-in for more than 60 days">
                          · Dormant
                        </span>
                      )}
                    </Td>
                    <Td className="py-3" align="right">
                      {user.id !== currentUser?.id && (
                        <Menu as="div" className="relative inline-block">
                          <MenuButton
                            aria-label={`Actions for ${user.name}`}
                            className="flex h-7 w-7 items-center justify-center rounded-full text-text-tertiary hover:bg-surface-tertiary hover:text-text-primary"
                          >
                            <Ellipsis className="h-4 w-4" />
                          </MenuButton>
                          <MenuItems
                            anchor="bottom end"
                            className="z-50 w-40 rounded-lg border border-border-light bg-surface-primary py-1 text-left shadow-medium [--anchor-gap:4px] focus:outline-none"
                          >
                            <MenuItem>
                              <button
                                type="button"
                                onClick={() => setModal({ kind: 'edit', user })}
                                className="block w-full px-3 py-1.5 text-left text-body-sm text-text-secondary data-[focus]:bg-surface-secondary"
                              >
                                Edit
                              </button>
                            </MenuItem>
                            <MenuItem>
                              <button
                                type="button"
                                onClick={() => setModal({ kind: 'access', user })}
                                className="block w-full px-3 py-1.5 text-left text-body-sm text-text-secondary data-[focus]:bg-surface-secondary"
                              >
                                Access
                              </button>
                            </MenuItem>
                            {user.isActive && (
                              <MenuItem>
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (confirm(`Deactivate ${user.name}?`)) deactivateUser.mutate(user.id);
                                  }}
                                  className="block w-full px-3 py-1.5 text-left text-body-sm text-error data-[focus]:bg-error-light"
                                >
                                  Deactivate
                                </button>
                              </MenuItem>
                            )}
                          </MenuItems>
                        </Menu>
                      )}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </Card>
        </>
      )}

      <InviteUserModal
        isOpen={modal?.kind === 'invite'}
        onClose={close}
        onInvite={(data) => inviteUser.mutate(data, { onSuccess: close })}
        isLoading={inviteUser.isPending}
      />

      {modal?.kind === 'edit' && (
        <EditUserModal
          user={modal.user}
          onClose={close}
          onSave={(data) => updateUser.mutate({ id: modal.user.id, data }, { onSuccess: close })}
          isLoading={updateUser.isPending}
        />
      )}

      <AuditLogModal isOpen={modal?.kind === 'audit'} onClose={close} />

      {modal?.kind === 'access' && <UserAccessModal user={modal.user} onClose={close} />}
      {modal?.kind === 'roleDefaults' && <RoleDefaultsModal onClose={close} />}
      {modal?.kind === 'teams' && <TeamsModal onClose={close} />}
    </PageShell>
  );
}

function InviteUserModal({
  isOpen,
  onClose,
  onInvite,
  isLoading,
}: {
  isOpen: boolean;
  onClose: () => void;
  onInvite: (data: { email: string; role: 'admin' | 'member' | 'viewer' }) => void;
  isLoading: boolean;
}) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'admin' | 'member' | 'viewer'>('member');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onInvite({ email, role });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Invite user">
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="user@example.com"
          required
        />

        <div>
          <label className="text-body-sm font-medium text-text-primary block mb-2">
            Role
          </label>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as typeof role)}
            className="w-full px-3 py-2 border border-border rounded-md bg-surface-primary text-text-primary text-body-sm focus:outline-none focus:ring-2 focus:ring-border-dark"
          >
            <option value="viewer">Viewer — read-only access</option>
            <option value="member">Member — standard access</option>
            <option value="admin">Admin — full access</option>
          </select>
        </div>

        <div className="flex justify-end gap-3 pt-4">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={!email || isLoading}>
            {isLoading ? 'Sending…' : 'Send invite'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function EditUserModal({
  user,
  onClose,
  onSave,
  isLoading,
}: {
  user: User;
  onClose: () => void;
  onSave: (data: { name?: string; role?: 'admin' | 'member' | 'viewer'; isActive?: boolean }) => void;
  isLoading: boolean;
}) {
  const [name, setName] = useState(user.name);
  const [role, setRole] = useState(user.role as 'admin' | 'member' | 'viewer');
  const [isActive, setIsActive] = useState(user.isActive);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({ name, role, isActive });
  };

  return (
    <Modal isOpen={true} onClose={onClose} title="Edit user">
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
        />

        <div>
          <label className="text-body-sm font-medium text-text-primary block mb-2">
            Role
          </label>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as typeof role)}
            className="w-full px-3 py-2 border border-border rounded-md bg-surface-primary text-text-primary text-body-sm focus:outline-none focus:ring-2 focus:ring-border-dark"
          >
            <option value="viewer">Viewer</option>
            <option value="member">Member</option>
            <option value="admin">Admin</option>
          </select>
        </div>

        <div className="flex items-center justify-between py-2">
          <span className="text-body-sm text-text-primary">Account active</span>
          <button
            type="button"
            role="switch"
            aria-checked={isActive}
            onClick={() => setIsActive(!isActive)}
            className={`relative inline-flex h-[18px] w-8 flex-none items-center rounded-full transition-colors ${
              isActive ? 'bg-text-secondary' : 'bg-border'
            }`}
          >
            <span
              className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow-sm transition-transform ${
                isActive ? 'translate-x-4' : 'translate-x-0.5'
              }`}
            />
          </button>
        </div>

        <div className="flex justify-end gap-3 pt-4">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={isLoading}>
            {isLoading ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

const AUDIT_ACTIONS: Record<string, string> = {
  'auth.login': 'Signed in',
  'auth.logout': 'Signed out',
  'auth.login_failed': 'Failed sign-in',
  'user.invite': 'Invited a user',
  'user.register': 'Accepted an invite',
  'user.update': 'Updated a user',
  'user.deactivate': 'Deactivated a user',
  'user_invite.resend': 'Resent an invite',
  'user_invite.delete': 'Cancelled an invite',
  'access.user.update': 'Changed a user’s access',
  'team.create': 'Created a team',
  'team.update': 'Updated a team',
  'team.delete': 'Deleted a team',
  'api_key.create': 'Created an API key',
  'api_key.delete': 'Revoked an API key',
  'password.change': 'Changed password',
  'profile.update': 'Updated profile',
  'google_account_connected': 'Connected Google account',
  'google_account_disconnected': 'Disconnected Google account',
  '2fa.enable': 'Turned on two-factor',
  '2fa.disable': 'Turned off two-factor',
  '2fa.verify': 'Passed two-factor check',
  '2fa.verify_failed': 'Failed two-factor check',
  '2fa.recovery_used': 'Used a recovery code',
  '2fa.recovery_failed': 'Failed recovery code',
  '2fa.recovery_codes_regenerated': 'Regenerated recovery codes',
  '2fa.admin_reset': 'Reset someone’s two-factor',
  '2fa.enforce_all': 'Required two-factor for everyone',
};

/** "team.create" → "Team create" for anything not in the map above. */
function auditActionLabel(log: AuditLog): string {
  const known = AUDIT_ACTIONS[log.action];
  if (known) return known;
  const words = log.action.replace(/[._]+/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

const AUDIT_PAGE_SIZE = 25;

function AuditLogModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const [page, setPage] = useState(1);
  const { data, isLoading, error, refetch } = useAuditLogs({ page, pageSize: AUDIT_PAGE_SIZE });
  const total = data?.pagination.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / AUDIT_PAGE_SIZE));

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Audit log" size="lg">
      <div className="max-h-[60vh] overflow-y-auto">
        {isLoading ? (
          <LoadingState />
        ) : error ? (
          <ErrorState title="Couldn’t load the audit log" error={error} onRetry={() => refetch()} />
        ) : !data?.logs.length ? (
          <EmptyState compact title="No audit events yet" />
        ) : (
          <ul className="divide-y divide-border-light">
            {data.logs.map((log) => (
              <li key={log.id} className="flex items-baseline justify-between gap-4 py-2">
                <span className="min-w-0 text-body-sm text-text-primary">
                  {auditActionLabel(log)}
                  <span className="text-text-tertiary"> · {log.user?.name || 'System'}</span>
                </span>
                <span className="flex-none text-body-xs text-text-tertiary">{formatDayMonthTime(log.createdAt)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
      <Pagination page={page} pageCount={pageCount} total={total} pageSize={AUDIT_PAGE_SIZE} onPage={setPage} />
    </Modal>
  );
}
